import React from "react";
import { Tool, DollarSign } from "react-feather";
import { useGetOrderQuery } from "../../sales/salesApi";
import { MAROON } from "../../../components/common/brandColors";

const num = (v) => Number(v) || 0;
const money = (n) => num(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const boxStyle = (color) => ({
  flex: "1 1 0",
  minWidth: 0,
  padding: "8px 10px",
  borderRadius: 8,
  border: `1px solid ${color}40`,
  borderLeft: `4px solid ${color}`,
  background: `${color}12`,
});
const headStyle = (color) => ({
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color,
  marginBottom: 4,
});
const WORK = "#0f766e";

/**
 * What the job is and what it is worth, on a Pending Assignment card.
 *
 * The queue row (sp_app_technical_work_pending_assignment) carries neither - and
 * widening that proc's SELECT list is a lockstep release with the live binary.
 * The row does carry the order's RecordGuid, and the order detail already returns
 * its priced service lines, so the box reads them from there instead.
 *
 * Guards lines are left out on purpose: this queue exists for the NON-guards part
 * of an order, and the guards figure is a monthly rate, not a job cost. An order
 * from before service-line pricing (Batch164) has names but no amounts - the
 * order's own alarms + other-services amounts stand in for the cost then.
 *
 * Renders nothing when the order cannot be read (no Sales read permission, or a
 * work item with no order behind it): the card is still fully usable without it.
 */
const PendingWorkCostBox = ({ salesOrderRecordGuid }) => {
  const { data: detail, isLoading, isError } = useGetOrderQuery(salesOrderRecordGuid, { skip: !salesOrderRecordGuid });

  if (!salesOrderRecordGuid || isError) return null;
  if (isLoading || !detail) {
    return <div className="text-muted mb-2" style={{ fontSize: 12 }}>Loading work details...</div>;
  }

  const order = detail.order || {};
  const priced = (detail.serviceLines || []).filter((l) => l.serviceKind !== "GUARDS");
  const named = priced.length > 0 ? priced : (detail.serviceTypes || []).filter((s) => s.serviceKind !== "GUARDS");

  const linesTotal = priced.reduce((sum, l) => sum + num(l.amount), 0);
  const workValue = linesTotal > 0 ? linesTotal : num(order.alarmsAmount) + num(order.otherServicesAmount);
  const vatRate = num(order.vatRate);

  return (
    <div className="d-flex gap-2 mb-2">
      <div style={boxStyle(WORK)}>
        <div style={headStyle(WORK)}>
          <Tool size={11} className="me-1" style={{ verticalAlign: "-1px" }} />
          Work
        </div>
        {named.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 12.5 }}>{order.servicesRequested || "Not specified on the order"}</div>
        ) : (
          named.map((l) => (
            <div key={l.serviceTypeId} className="d-flex justify-content-between gap-2" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              <span className="fw-semibold text-truncate" title={l.serviceTypeName}>
                {l.serviceTypeName}
                {num(l.qty) > 0 && <span className="text-muted fw-normal"> × {num(l.qty)}</span>}
              </span>
              {num(l.amount) > 0 && <span style={{ whiteSpace: "nowrap" }}>{money(l.amount)}</span>}
            </div>
          ))
        )}
      </div>

      <div style={{ ...boxStyle(MAROON), flex: "0 0 38%", textAlign: "right" }}>
        <div style={headStyle(MAROON)}>
          <DollarSign size={11} style={{ verticalAlign: "-1px" }} />
          Cost
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: MAROON, lineHeight: 1.2, fontFeatureSettings: '"tnum"' }}>
          {workValue > 0 ? money(workValue) : "—"}
        </div>
        <div className="text-muted" style={{ fontSize: 11 }}>
          {workValue > 0 ? (vatRate > 0 ? `+ ${vatRate}% VAT` : "excl. VAT") : "not priced"}
        </div>
      </div>
    </div>
  );
};

export default PendingWorkCostBox;
